/**
 * Forehead ROI extraction from MediaPipe face landmarks.
 *
 * The forehead is the preferred skin ROI for rPPG because it is:
 *   - relatively flat (less curvature → more uniform lighting)
 *   - usually hair-free (compared to cheeks which can be occluded)
 *   - exposed and easy to see even with glasses/masks
 *
 * We take the axis-aligned bounding box of a handful of forehead landmark
 * points from the MediaPipe face mesh, then shrink it vertically to stay
 * inside the skin region (away from hair at the top and eyebrows at the
 * bottom).
 */

/** Minimal subset of MediaPipe's NormalizedLandmark that we actually use. */
export interface Landmark {
  x: number;
  y: number;
  z?: number;
}

/**
 * Forehead landmark indices on the MediaPipe face mesh (468-point model).
 *
 *   10  = top-center of forehead (near hairline)
 *   151 = glabella (just above the bridge of the nose, between eyebrows)
 *   67  = left upper forehead
 *   297 = right upper forehead
 *   109 = left forehead (above medial eyebrow)
 *   338 = right forehead (above medial eyebrow)
 */
const FOREHEAD_LANDMARKS = [10, 67, 109, 151, 338, 297];

export interface RoiRect {
  x: number;
  y: number;
  w: number;
  h: number;
}

/**
 * Compute the forehead ROI in pixel coordinates given normalized face
 * landmarks and the source video dimensions. Returns `null` when the
 * landmarks are missing or the resulting rect is too small / out of bounds.
 */
export function foreheadRoi(
  landmarks: Landmark[] | null | undefined,
  videoWidth: number,
  videoHeight: number,
): RoiRect | null {
  if (!landmarks || landmarks.length < 468) return null;

  let minX = Infinity, maxX = -Infinity;
  let minY = Infinity, maxY = -Infinity;
  for (const idx of FOREHEAD_LANDMARKS) {
    const lm = landmarks[idx];
    if (!lm) return null;
    if (lm.x < minX) minX = lm.x;
    if (lm.x > maxX) maxX = lm.x;
    if (lm.y < minY) minY = lm.y;
    if (lm.y > maxY) maxY = lm.y;
  }

  // Shrink vertically to stay clear of hair (top) and eyebrows (bottom).
  const h = maxY - minY;
  minY += h * 0.2;
  maxY -= h * 0.15;

  // Shrink horizontally slightly to avoid the temple / hairline on the sides.
  const w = maxX - minX;
  minX += w * 0.05;
  maxX -= w * 0.05;

  // Convert normalized [0..1] to pixel space.
  const px = Math.max(0, Math.floor(minX * videoWidth));
  const py = Math.max(0, Math.floor(minY * videoHeight));
  const pw = Math.max(1, Math.floor((maxX - minX) * videoWidth));
  const ph = Math.max(1, Math.floor((maxY - minY) * videoHeight));

  if (px + pw > videoWidth || py + ph > videoHeight) return null;
  if (pw < 10 || ph < 6) return null;

  return { x: px, y: py, w: pw, h: ph };
}

/**
 * Compute mean [r, g, b] from an ImageData. Each channel is averaged over
 * all pixels in the image (no skin-color filtering — ROI is already
 * restricted to forehead skin by `foreheadRoi`).
 */
export function meanRgbFromImageData(imageData: ImageData): [number, number, number] {
  const data = imageData.data;
  const n = data.length / 4;
  if (n === 0) return [0, 0, 0];

  let r = 0, g = 0, b = 0;
  for (let i = 0; i < data.length; i += 4) {
    r += data[i];
    g += data[i + 1];
    b += data[i + 2];
  }
  return [r / n, g / n, b / n];
}
